import React, { useEffect, useMemo } from "react";

import { add, isBefore } from "date-fns";
import { useFormContext } from "react-hook-form";

import { FartherManagedAccount } from "@fartherfinance/frontend/api/Accounts/Types";
import { ExternalAccountExtended } from "@fartherfinance/frontend/api/ExternalAccount/hooks/useExternalAccountsExtended";
import { BalanceInfo } from "@fartherfinance/frontend/api/PerformanceGroups/hooks/useAllAccountBalances";

import { CreateTransferDepositFormData } from "../const";
import {
  createAccountLabel,
  createFundingAccountLabel,
  findAccountBalance,
  isAccountTypeIRA,
} from "../utils";
import useApplicationMode from "@src/multiCustodian/hooks/useApplicationMode";
import FormDropdownField from "@src/sharedComponents/Forms/FormDropdownField";
import FormNumberField from "@src/sharedComponents/Forms/FormNumberField";
import FormTextField from "@src/sharedComponents/Forms/FormTextField";
import removeCommas from "@src/utils/CommaHelperFunctions/removeCommas";
import { formNumberFieldUpTo2DecimalsWithCommasCustomRegex } from "@src/utils/formValidation";

const createYearOptions = (currentTime: Date): { label: string }[] => {
  const thisYear = currentTime.getFullYear();

  // We can contribute to the previous year up to April 15th
  const isTodayBefore16April = isBefore(new Date(), new Date(thisYear, 3, 16));

  const lastYear = add(new Date(), { years: -1 }).getFullYear();

  return [
    { label: thisYear.toString() },
    ...(isTodayBefore16April ? [{ label: lastYear.toString() }] : []),
  ];
};

interface CreateTransferDepositFormProps {
  fundingAccount?: ExternalAccountExtended | null;
  managedAccounts: FartherManagedAccount[];
  allAccountBalances: BalanceInfo[];
}

const CreateTransferDepositForm: React.FC<CreateTransferDepositFormProps> = ({
  fundingAccount,
  managedAccounts,
  allAccountBalances,
}) => {
  const { isImpersonationMode } = useApplicationMode();

  const form = useFormContext<CreateTransferDepositFormData>();
  const { watch, setError } = form;

  const depositAccount = watch("depositAccount");

  const isAccountIRA = useMemo(() => {
    const { accountType } = depositAccount?.account.accountDetails ?? {};

    return accountType && isAccountTypeIRA(accountType);
  }, [depositAccount]);

  const fundingAccountLabel = useMemo(
    () => createFundingAccountLabel(fundingAccount),
    [fundingAccount]
  );

  const validACHAccountsList = useMemo(() => {
    const validACHAccountIds =
      fundingAccount?.fundingDetails?.validAchAccountIds ?? [];

    const validACHAccounts = managedAccounts.filter(
      (account) =>
        validACHAccountIds.includes(account.virtualAccountId) &&
        account.sharedBy.length === 0
    );

    return validACHAccounts.map((account) => {
      const accountBalance = findAccountBalance(
        account.virtualAccountId,
        allAccountBalances
      );

      return {
        label: createAccountLabel(account, accountBalance, isImpersonationMode),
        account,
      };
    });
  }, [
    fundingAccount,
    managedAccounts,
    allAccountBalances,
    isImpersonationMode,
  ]);

  useEffect(() => {
    if (fundingAccount === null) {
      setError("withdrawalAccountLabel", {
        type: "custom",
        message: "No funding account set up",
      });
    }
  }, [setError, fundingAccount]);

  return (
    <>
      <FormTextField
        name="withdrawalAccountLabel"
        label="Withdraw from"
        labelTooltip="To change your bank account, please contact your wealth advisor."
        compactTooltip
        readonly
        defaultValue={fundingAccountLabel}
      />

      <FormDropdownField
        name="depositAccount"
        label="Deposit to"
        disableSearch
        required="Please select a valid deposit account"
        values={validACHAccountsList}
      />

      <FormNumberField
        name="depositAmount"
        label="Amount"
        labelTooltip="ACH transfers are limited to $100,000 per day."
        compactTooltip
        startAdornment={"$"}
        customRegex={formNumberFieldUpTo2DecimalsWithCommasCustomRegex}
        required="Please provide a transfer amount"
        rules={{
          validate: {
            isMinimum: (value) => {
              const commaLessNumber = Number(removeCommas(value));

              return commaLessNumber < 1
                ? "Amount needs to be greater than or equal to $1"
                : true;
            },
            isMaximum: (value) => {
              const commaLessNumber = Number(removeCommas(value));

              return commaLessNumber > 100_000
                ? "Amount needs to be less than or equal to $100,000"
                : true;
            },
          },
        }}
        autoAddCommas={true}
      />

      {isAccountIRA && (
        <FormDropdownField
          name="depositContributionYear"
          label="Contribution Year"
          values={createYearOptions(new Date())}
        />
      )}
    </>
  );
};

export default CreateTransferDepositForm;
